// ─────────────────────────────────────────────
//  RoomAlertButtons — the Emergency + Urgency pair for a personal room space.
//
//  Shown in the provider's "My Room" (ProviderPage). Both actions confirm via
//  the Phase 1 Modal (never a native dialog) to prevent a mis-tap firing a
//  floor-wide alarm.
//    • Emergency → loud broadcast (supervisors + AIC), board goes red + blinks.
//    • Urgency   → silent, private to the room's assigned doctor. Disabled when
//      no doctor is assigned to the room yet.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { useAlertCenter } from "../context/AlertContext";
import { useToast } from "../context/ToastContext";
import Modal from "./Modal";
import Button from "./Button";
import tokens from "../theme/tokens";

const { color, radius, font, space } = tokens;

export default function RoomAlertButtons({ roomId, roomName, doctorId }) {
  const { raiseEmergency, raiseUrgency } = useAlertCenter();
  const toast = useToast();
  const [confirm, setConfirm] = useState(null); // "emergency" | "urgency" | null
  const [busy, setBusy] = useState(false);
  const hasDoctor = !!doctorId;

  const fireEmergency = async () => {
    setBusy(true);
    try {
      const r = await raiseEmergency({ roomId, roomName, doctorId });
      if (r.ok) toast.success(`🚨 Emergency sent to ${r.targetCount} on the team.`);
      else if (r.reason === "duplicate") toast.info("An emergency is already active for this room.");
      else if (r.reason === "no_targets") toast.error("No doctors or AIC on today's roster to alert. Call directly.");
      else toast.error("Could not send the emergency. Call directly if critical.");
      setConfirm(null);
    } catch (e) {
      console.error("Emergency failed:", e);
      toast.error("Could not send the emergency. Call directly if critical.");
    } finally {
      setBusy(false);
    }
  };

  const fireUrgency = async () => {
    setBusy(true);
    try {
      const r = await raiseUrgency({ roomId, roomName, doctorId });
      if (r.ok) toast.success("Silent urgent ping sent to your doctor.");
      else toast.error("No doctor assigned to this room yet.");
      setConfirm(null);
    } catch (e) {
      console.error("Urgency failed:", e);
      toast.error("Could not send the urgent ping.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={s.panel}>
      <div style={s.title}>Raise an Alert</div>
      <div style={s.row}>
        <button style={s.emBtn} onClick={() => setConfirm("emergency")}>
          <span style={s.btnIcon} aria-hidden="true">🚨</span>
          <span style={s.btnLabel}>Emergency</span>
          <span style={s.btnSub}>Alerts all doctors + AIC. Board turns red.</span>
        </button>
        <button
          style={{ ...s.urBtn, ...(hasDoctor ? {} : s.disabled) }}
          onClick={() => hasDoctor && setConfirm("urgency")}
          disabled={!hasDoctor}
          title={hasDoctor ? "Silent ping to your doctor" : "No doctor assigned to this room yet"}
        >
          <span style={s.btnIcon} aria-hidden="true">⚡</span>
          <span style={s.btnLabel}>Urgency</span>
          <span style={s.btnSub}>{hasDoctor ? "Silent, private to your doctor." : "No doctor assigned yet."}</span>
        </button>
      </div>

      <Modal
        isOpen={confirm === "emergency"}
        onClose={() => setConfirm(null)}
        title="Send EMERGENCY alert?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
            <Button variant="danger" onClick={fireEmergency} disabled={busy}>
              {busy ? "Sending…" : "🚨 Send emergency"}
            </Button>
          </>
        }
      >
        This immediately alerts <strong>every supervising doctor and the AIC</strong>, blinks the
        OR board, and turns {roomName || "your room"} red. Use for a true emergency.
      </Modal>

      <Modal
        isOpen={confirm === "urgency"}
        onClose={() => setConfirm(null)}
        title="Send silent urgent ping?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={fireUrgency} disabled={busy}>
              {busy ? "Sending…" : "Send ping"}
            </Button>
          </>
        }
      >
        This quietly notifies <strong>only your assigned doctor</strong> — no board alert, no blink,
        nobody else sees it.
      </Modal>
    </div>
  );
}

const s = {
  panel: { background: color.surface.card, border: `1px solid ${color.border.default}`, borderRadius: radius.md, padding: 16, marginBottom: 20 },
  title: { fontSize: 14, fontWeight: font.weight.bold, marginBottom: 12, color: color.text.primary },
  row: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: space.md },
  emBtn: {
    display: "flex", flexDirection: "column", gap: 4, textAlign: "left",
    background: "#fdecec", border: `1px solid ${color.accent.error}`, borderLeft: `4px solid ${color.accent.error}`,
    borderRadius: radius.md, padding: 16, cursor: "pointer", fontFamily: "inherit", color: "inherit",
  },
  urBtn: {
    display: "flex", flexDirection: "column", gap: 4, textAlign: "left",
    background: "#fff7ed", border: `1px solid ${color.accent.closing}`, borderLeft: `4px solid ${color.accent.closing}`,
    borderRadius: radius.md, padding: 16, cursor: "pointer", fontFamily: "inherit", color: "inherit",
  },
  disabled: { opacity: 0.5, cursor: "not-allowed" },
  btnIcon: { fontSize: 22 },
  btnLabel: { fontSize: 15, fontWeight: font.weight.bold, color: color.text.primary },
  btnSub: { fontSize: font.size.label, color: color.text.muted },
};
